"use client";

import DateFiled from "@/components/controlled-table/date-field";
import StatusField from "@/components/controlled-table/status-field";
import UsersFilter from "@/components/filters/users-filter";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";
import { useMemo } from "react";
import { PiMagnifyingGlassBold, PiTrashDuotone } from "react-icons/pi";
import { useMedia } from "react-use";
import { Input, Button, SelectOption, Badge } from "rizzui";
import useMobile from "@/hooks/use-mobile";

type FilterElementProps = {
  isFiltered: boolean;
  filters: { [key: string]: any };
  updateFilter: (columnId: string, filterValue?: string | any[]) => void;
  handleReset: () => void;
  onSearch: (searchTerm: string) => void;
  searchTerm: string;
  handleSort?: (value: string) => void;
};

type BadgeColor =
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "primary"
  | "secondary";

const scoreRange = [
  { value: "fromScore=0", label: "All", color: "info" },
  { value: "fromScore=81", label: "Hot", color: "danger" },
  { value: "fromScore=50&toScore=80", label: "Warm", color: "warning" },
  { value: "fromScore=0&toScore=0", label: "Agent/Spam", color: "primary" },
];

type ScoreKey =
  | "fromScore=0"
  | "fromScore=81"
  | "fromScore=50&toScore=80"
  | "fromScore=0&toScore=0";

const scoreFields: Record<ScoreKey, { from: number; to: number | null }> = {
  "fromScore=0": { from: 0, to: null },
  "fromScore=81": { from: 81, to: null },
  "fromScore=50&toScore=80": { from: 50, to: 80 },
  "fromScore=0&toScore=0": { from: 0, to: 0 },
};

function renderScoreOptionBadge(
  option: (typeof scoreRange)[number] | undefined,
  hideBadge: boolean,
) {
  if (hideBadge || !option) return null;
  return (
    <Badge renderAsDot color={(option.color as BadgeColor) || "primary"} />
  );
}

function getScoreDisplayValue(selected: string) {
  const selectedOption = scoreRange?.find(
    (option) => option.value === selected,
  );
  return selectedOption?.label ? (
    <div className="flex items-center justify-start gap-1">
      {renderScoreOptionBadge(
        selectedOption,
        selectedOption?.value === "fromScore=0",
      )}
      {selectedOption?.label}
    </div>
  ) : (
    <span>Filter by Score</span>
  );
}

function getScoreOptionDisplayValue(selected: SelectOption) {
  const selectedOption = scoreRange?.find(
    (option) => option.value === selected.value,
  );
  return (
    <div className="flex items-center justify-start gap-1">
      {renderScoreOptionBadge(
        selectedOption,
        selectedOption?.value === "fromScore=0",
      )}
      {selectedOption?.label}
    </div>
  );
}

function processFilterScore(
  key: ScoreKey,
  updateFilter: (columnId: string, filterValue?: string | any[]) => void,
) {
  if (key in scoreFields) {
    const { from, to } = scoreFields[key as ScoreKey];
    updateFilter("fromScore", from.toString());
    updateFilter("toScore", to?.toString());
  } else {
    updateFilter("fromScore", "0");
  }
}

export default function FilterElement({
  isFiltered,
  handleReset,
  filters,
  updateFilter,
  onSearch,
  searchTerm,
  handleSort,
}: FilterElementProps) {
  const isMediumScreen = useMedia("(max-width: 1860px)", false);
  const { isMobile } = useMobile();

  const { profile } = useProfile();
  const isAdmin = profile?.role === Roles.ADMIN;

  const selectedScoreValue = useMemo(() => {
    let value = `fromScore=${filters?.["fromScore"] ?? 0}`;
    if (filters?.["toScore"]) {
      value += `&toScore=${filters?.["toScore"]}`;
    }
    return value;
  }, [filters]);

  return (
    <div className="mb-4">
      <div className="flex md:flex-row mb-4 items-end justify-between flex-col w-full flex-wrap gap-4">
        <div className="flex md:flex-row items-end flex-col w-full md:w-fit gap-2">
          {isMobile && !isAdmin ? (
            <></>
          ) : (
            <>
              <DateFiled
                className="w-full"
                selected={filters?.["startDate"]}
                selectsRange={false}
                onChange={(date: any) => {
                  updateFilter("startDate", date || undefined);
                }}
                placeholderText="Select created date"
                {...(isMediumScreen && {
                  inputProps: {
                    label: "Start Date",
                    labelClassName: "font-medium text-gray-700",
                  },
                })}
              />
              <DateFiled
                className="w-full"
                selected={filters?.["endDate"]}
                onChange={(date: any) => {
                  updateFilter("endDate", date || undefined);
                }}
                selectsRange={false}
                placeholderText="Select created date"
                {...(isMediumScreen && {
                  inputProps: {
                    label: "End Date",
                    labelClassName: "font-medium text-gray-700",
                  },
                })}
              />
            </>
          )}

          <div className="flex w-full md:w-fit relative h-fit">
            <StatusField
              label="Filter by Status"
              className="md:min-w-[200px] md:w-fit h-fit w-full"
              options={scoreRange}
              value={selectedScoreValue}
              dropdownClassName="!z-10"
              placeholder="Filter by Status"
              getOptionValue={(option: SelectOption) => option.value}
              displayValue={getScoreDisplayValue}
              getOptionDisplayValue={getScoreOptionDisplayValue}
              onChange={(score: ScoreKey) => {
                processFilterScore(score, updateFilter);
              }}
            />
          </div>

          {isAdmin && (
            <UsersFilter
              onChange={(user) => {
                updateFilter("userId", user?.id || "");
              }}
              label="Filter by agent"
              value={filters?.userId}
              className="lg:min-w-[250px] w-full"
              placeholder="Select agent"
            />
          )}

          {isFiltered && (
            <Button
              size="sm"
              onClick={handleReset}
              variant="flat"
              className="self-end w-full lg:w-fit min-h-10 text-sm"
            >
              <PiTrashDuotone className=" me-1.5 h-5 w-5" /> Clear
            </Button>
          )}
          <Input
            type="search"
            placeholder="Search by customer's name, email..."
            value={searchTerm}
            onClear={() => onSearch("")}
            onChange={(event) => onSearch(event.target.value)}
            prefix={<PiMagnifyingGlassBold className="h-4 w-4" />}
            rounded="lg"
            clearable
            className="md:w-fit min-w-[250px] w-full"
          />
        </div>
      </div>
    </div>
  );
}
